import { useEffect, useMemo, useRef, useState } from 'react';

import {
  selectClosureTimeslots,
  selectRouteMetrics,
  selectScenarioStatus,
} from 'state/detourSlices';
import { useAppSelector } from 'state/hooks';
import {
  selectCurrentProjectInfo,
  selectCurrentScenarioId,
  selectScenarioSetupUIMode,
  selectShortestAvailableBin,
} from 'state/workflowSlice';

import { PROCESS_STATUS, ScenarioSetupUIMode } from 'appConstants';
import { SyncedHighchartsReact } from 'features/chart/SyncedHighchartsReact';
import { useChartResize } from 'features/chart/useChartResize';
import { formatTraveltime } from 'features/common/utils';
import LoadingDotAnimation from 'features/spinner/LoadingDotAnimation';
import { NoRouteSelectedPanel } from 'features/workflow_common/RouteInstructions/NoRouteSelectedPanel';
import Highcharts from 'highcharts';

import { comparison_color } from '../chart/chartOptions';
import {
  getFormattedTime,
  planningChartOptions,
} from '../chart/planningChartOptions';
import { travelTimeChartOptions } from '../chart/travelTimeChartOptions';
import { binEndFromTs } from '../common/slotsUtils';
import {
  ScenarioTravelTimeChartDataExpander,
  useScenarioTravelTimeDataProvider,
} from './ScenarioTravelTimeProvider';
import { ClosureTimeslot, closureDayNames } from './utils';

import styles from 'features/chart/Chart.module.css';

const MS_IN_HOUR = 3600000;
const MS_IN_DAY = MS_IN_HOUR * 24;
const MIDNIGHT = '12:00';

function closureDayForTs(
  ts: number,
  firstBin: number,
  dayNames: string[],
): string {
  const dayIndex = Math.min(
    Math.floor((ts - firstBin) / MS_IN_DAY),
    dayNames.length - 1,
  );
  return dayNames[dayIndex];
}

/** display dayname for the beginning of the day */
function makeClosureAxisLabeller(
  timeslots: ClosureTimeslot[],
  firstBin: number,
  min: number,
) {
  const dayNames = closureDayNames(timeslots);
  return function closureAxisLabelFormatter(
    this: Highcharts.AxisLabelsFormatterContextObject,
  ) {
    let label = this.axis.defaultLabelFormatter.call(this);
    const ts = this.value as number;
    const dayIndex = Math.min(
      Math.floor((ts - firstBin) / MS_IN_DAY),
      dayNames.length - 1,
    );
    const isFirstBin = ts === min;
    if (isFirstBin || (label === MIDNIGHT && dayIndex > 0)) {
      label += `<br/>${dayNames[dayIndex]}`;
    }
    return label;
  };
}

export const makeFormatTooltip = (
  bin_size: number,
  timeslots: ClosureTimeslot[],
  firstBin: number,
  value_formatter,
) => {
  const dayNames = closureDayNames(timeslots);
  function formatTooltip(
    this: Highcharts.TooltipFormatterContextObject,
    tooltip: Highcharts.Tooltip,
  ) {
    const ts = (this.points[1]?.x || this.points[0]?.x) as number;
    const date = getFormattedTime(ts);
    const end_time = getFormattedTime(binEndFromTs(ts as number, bin_size));
    const day = closureDayForTs(ts, firstBin, dayNames);
    let output = `
        <div class="chart-legend" style="font-size: 15px;">
        <table>
            <tr><td><h1>${day}, ${date} - ${end_time}</h1></td><td>Traveltime</td></tr>
            `;
    if (this.points[1]) {
      output += `
        <tr>
            <td>
                <span style="color:${
                  this.points[1].point.color
                }">●</span>${this.points[1].series.name.replace(
                  'traveltime',
                  '',
                )}
            </td>
            <td>
                <b>${value_formatter(this.points[1].y)}</b>
            </td>
        </tr>`;
    }
    if (this.points[0]) {
      output += `
        <tr>
            <td>
                <span style="color:${
                  this.points[0].point.color
                }">●</span>${this.points[0].series.name.replace(
                  'traveltime',
                  '',
                )}
            </td>
            <td>
                <b>${value_formatter(this.points[0].y)}</b>
            </td>
        </tr>`;
    }
    if (this.points[2]) {
      output += `
        <tr>
            <td>
                <span style="color:${
                  this.points[2].point.color
                }">●</span>${this.points[2].series.name.replace(
                  'traveltime',
                  '',
                )}
            </td>
            <td>
                <b>${value_formatter(this.points[2].y)}</b>
            </td>
        </tr>`;
    }
    return `${output}</table></div>`;
  }
  return formatTooltip;
};

const scenarioTravelTimeChartOptions = {
  ...travelTimeChartOptions,
  series: [
    ...travelTimeChartOptions.series,
    {
      id: 'detour_tt',
      type: 'spline',
      data: [],
      name: 'Through-closure traveltime',
      color: comparison_color,
      lineWidth: 3,
      marker: { enabled: false, symbol: 'circle' },
      dashStyle: 'dash',
      showInLegend: false,
    },
    {
      id: 'detour_tt_legend', // Placeholder series for the legend because normal dashes don't really show up in the legend nicely
      type: 'spline',
      data: [],
      name: 'Through-closure traveltime',
      color: comparison_color,
      lineWidth: 3,
      marker: { enabled: false, symbol: 'circle' },
      dashStyle: 'shortdot',
    },
  ],
};

const TYPICAL_IDX = 3;
const SCENARIO_IDX = 2;
const TICK_STEP = 3 * MS_IN_HOUR;

function ScenarioTravelTimeChart() {
  const binSize = useAppSelector(selectShortestAvailableBin);
  const project_info = useAppSelector(selectCurrentProjectInfo);

  const chartComponent = useRef(null); // so we can call reflow
  useChartResize(chartComponent);
  const [chartOptions, setChartOptions] = useState(
    scenarioTravelTimeChartOptions as any,
  );

  // ttData from REST api
  const { scenarioData, nonDetourData, loading } =
    useScenarioTravelTimeDataProvider();
  const closureTimeslots = useAppSelector(selectClosureTimeslots);

  const dataExpander = useMemo(
    () =>
      new ScenarioTravelTimeChartDataExpander(
        binSize,
        project_info.uses_middle_east_day_types,
      ),
    [binSize],
  );

  // memoize expanded segmentSpeeds
  const segmentTTs = useMemo(
    () =>
      dataExpander.expandTravelTimes(
        scenarioData,
        SCENARIO_IDX,
        closureTimeslots,
      ),
    [scenarioData, closureTimeslots, dataExpander],
  );
  // console.log(`memoized segmentSpeeds ${JSON.stringify(segmentSpeeds)}`);

  // memoize typicalSpeeds
  const comparisonTTs = useMemo(() => {
    return dataExpander?.expandTravelTimes(
      scenarioData,
      TYPICAL_IDX,
      closureTimeslots,
    );
  }, [scenarioData, closureTimeslots, dataExpander]);
  // console.log(`###  memoized typicalSpeeds ${JSON.stringify(typicalSpeeds)}`);

  const detourTTs = useMemo(() => {
    if (!nonDetourData) return undefined;
    return dataExpander?.expandTravelTimes(
      nonDetourData,
      TYPICAL_IDX,
      closureTimeslots,
    );
  }, [nonDetourData, closureTimeslots, dataExpander]);

  useEffect(() => {
    const { min, max } = dataExpander.closureChartXRange(closureTimeslots);
    const series = [
      {
        id: 'typical_tt',
        name: 'Comparison traveltime',
        data: comparisonTTs || [],
        marker: { enabled: false },
      },
      {
        id: 'segment_tt',
        data: segmentTTs || [],
        name: 'Scenario traveltime',
        marker: { enabled: false },
      },
      {
        id: 'detour_tt',
        data: detourTTs || [],
        hide: !detourTTs,
        marker: { enabled: false },
      },
      {
        id: 'detour_tt_legend',
        hide: !detourTTs,
        showInLegend: !!detourTTs,
      },
    ];
    setChartOptions((prev) => ({
      ...prev,
      time: {
        useUTC: true,
      },
      xAxis: {
        ...planningChartOptions.xAxis,
        min,
        max,
        endOnTick: false,
        startOnTick: false,
        tickPositioner() {
          const positions = [];
          let tick = Math.ceil(min / TICK_STEP) * TICK_STEP;
          while (tick <= max) {
            positions.push(tick);
            tick += TICK_STEP;
          }
          // closure_end needs to have a tick
          if (!positions.length || positions.at(-1) !== max) {
            const diffMs = max - positions.at(-1);
            // if less than an hour since the last tick, replace it (case 18:00 18:45, tick only at 18:45)
            if (diffMs < MS_IN_HOUR) positions[positions.length - 1] = max;
            else positions.push(max);
          }
          return positions;
        },
        labels: {
          ...planningChartOptions.xAxis.labels,
          formatter: makeClosureAxisLabeller(
            closureTimeslots,
            dataExpander.firstBin,
            min,
          ),
        },
      },
      series,
      tooltip: {
        formatter: makeFormatTooltip(
          dataExpander.binSize,
          closureTimeslots,
          dataExpander.firstBin,
          formatTraveltime,
        ),
      },
    }));
  }, [segmentTTs, comparisonTTs, detourTTs, closureTimeslots, dataExpander]);

  return (
    <div
      className={styles.chart}
      style={{ width: '100%', height: 'fit-content' }}
    >
      {loading && (
        <div className={styles.loading_wrapper}>
          <LoadingDotAnimation />
        </div>
      )}
      <SyncedHighchartsReact
        ref={chartComponent}
        highcharts={Highcharts}
        containerProps={{
          style: {
            height: '250px',
            width: '100%',
            visibility: loading ? 'hidden' : 'unset',
          },
        }}
        options={chartOptions}
      />
    </div>
  );
}

export function CollapseScenarioTravelTimeChart() {
  const routeMetrics = useAppSelector(selectRouteMetrics);
  const currentScenarioId = useAppSelector(selectCurrentScenarioId);
  const uiMode = useAppSelector(selectScenarioSetupUIMode);
  const scenarioStatus = useAppSelector(selectScenarioStatus);

  const collapseOpen = Boolean(
    uiMode === ScenarioSetupUIMode.Scenarios &&
    routeMetrics &&
    currentScenarioId &&
    scenarioStatus === PROCESS_STATUS.DONE,
  );

  const no_selection = NoRouteSelectedPanel();
  if (!collapseOpen) return null;
  if (no_selection) return no_selection;
  return <ScenarioTravelTimeChart />;
}
